import { useState, useCallback, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import App from "./App";
import "./index.css";
import { SplashScreen } from "./components/SplashScreen";
import { InstallPrompt } from "./components/InstallPrompt";
import { ProfilePromptPreview } from "./components/ProfilePromptPreview";
import { ErrorBoundary } from "./components/ErrorBoundary";

// ─── Chunk-load auto-reload ───────────────────────────────────────────────────
// After a new deploy the service worker may serve the new HTML (new chunk
// filenames) while old JS chunks are still in the SW cache. Dynamic imports
// then fail with a "Failed to fetch dynamically imported module" error.
// Catch that once and reload — the new SW will then serve the fresh chunks.
const RELOAD_KEY = 'dp_chunk_reload';
function handleChunkError(msg: string) {
  if (/dynamically imported module|Loading chunk|Failed to fetch/.test(msg)) {
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
    }
  }
}
window.addEventListener('error', (e) => handleChunkError(e.message ?? ''));
window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message ?? String(e.reason ?? '');
  handleChunkError(msg);
});

// ─── Safe storage helpers ─────────────────────────────────────────────────────
function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* private mode */ }
}

// ─── Clerk publishable key ────────────────────────────────────────────────────
const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!publishableKey) {
  console.error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const isProfilePreview =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === 'profile';

function Root() {
  // Invalidate course-definition cache when the deployed version changes.
  // If /api/version returns a different commit SHA than what is stored, we
  // remove the cached course list so SetupScreen always starts fresh after a deploy.
  // Fire-and-forget: version check failure is non-blocking.
  useEffect(() => {
    const storedVersion = localStorage.getItem('dp_last_version');
    fetch('/api/version', { credentials: 'include' })
      .then(r => r.json())
      .then(({ commit }: { commit?: string }) => {
        if (commit && commit !== storedVersion) {
          queryClient.removeQueries({ queryKey: ['/api/courses/local'] });
          try { localStorage.setItem('dp_last_version', commit); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  const [showSplash, setShowSplash] = useState(
    () => !safeSessionGet('dp_splash_shown')
  );

  const handleSplashComplete = useCallback(() => {
    safeSessionSet('dp_splash_shown', '1');
    setShowSplash(false);
  }, []);

  if (isProfilePreview) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ProfilePromptPreview />
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
    <>
      {showSplash ? (
        <SplashScreen onComplete={handleSplashComplete} />
      ) : (
        <ErrorBoundary>
          <ClerkProvider publishableKey={publishableKey || ""}>
            <App />
          </ClerkProvider>
        </ErrorBoundary>
      )}
      <InstallPrompt />
    </>
  );
}

createRoot(document.getElementById("root")!).render(<Root />);
